import {
  useDeletePreviousShareVersionsMutation,
  GetVersionHistoryDocument,
  DeletePreviousShareVersionsMutationVariables,
  useApolloClient,
  GetVersionHistoryQueryVariables,
  GetVersionHistoryQuery,
  VersionListItemWithOrderFragment,
} from '@sketch/gql-types'
import { versionedRoutes } from '@sketch/modules-common'
import { useHistory, useLocation } from 'react-router'
import { extractVersionInformation } from '../ShareVersionContext/extractVersionInformation'

type PreserveVersionsOption =
  DeletePreviousShareVersionsMutationVariables['preserveVersions']

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again later'

interface UsePreviousVersionsDeleteProps {
  shareIdentifiers: string[]
  versionIdentifier?: string
  preserveVersions: PreserveVersionsOption
  skipNavigation?: boolean
  onError(message: string): void
  onCompleted(deletedCount: number): void
}

export const usePreviousVersionsDelete = (
  props: UsePreviousVersionsDeleteProps
) => {
  const {
    onError,
    shareIdentifiers,
    versionIdentifier,
    preserveVersions,
    skipNavigation,
    onCompleted,
  } = props

  const apolloClient = useApolloClient()
  const history = useHistory()
  const location = useLocation()

  // Get the currently viewing version short ID directly from the URL
  // This is more reliable than using useVersioning() since the modal
  // may be rendered outside of ShareVersionProvider context
  const { versionShortId: currentlyViewingVersionShortId } =
    extractVersionInformation(location.pathname)

  const [mutate, ...rest] = useDeletePreviousShareVersionsMutation({
    variables: { shareIdentifiers, versionIdentifier, preserveVersions },
    onCompleted: async data => {
      if (!data || !data.deletePreviousShareVersions) {
        onError(GENERIC_ERROR_MESSAGE)
        return
      }

      const deletedCount = data.deletePreviousShareVersions.deletedCount || 0

      if (deletedCount === 0) {
        onCompleted(deletedCount)
        return
      }

      // Navigation and cache update only make sense for a single share
      if (shareIdentifiers.length === 1) {
        const shareIdentifier = shareIdentifiers[0]

        const { data: newVersionData } = await apolloClient.query<
          GetVersionHistoryQuery,
          GetVersionHistoryQueryVariables
        >({
          query: GetVersionHistoryDocument,
          variables: { shareIdentifier, after: null },
          fetchPolicy: 'no-cache',
        })

        const currentlyViewingVersionExists =
          currentlyViewingVersionShortId &&
          newVersionData.share?.versionHistory.entries.find(version => {
            if (version.__typename === 'Version') {
              return version.shortId === currentlyViewingVersionShortId
            }

            return false
          })

        // Find the selected version by identifier, or fall back to the first (latest) version
        const selectedVersion = versionIdentifier
          ? (newVersionData.share?.versionHistory.entries.find(version => {
              if (version.__typename === 'Version') {
                return version.identifier === versionIdentifier
              }
              return false
            }) as VersionListItemWithOrderFragment)
          : (newVersionData.share?.versionHistory.entries.find(
              version => version.__typename === 'Version'
            ) as VersionListItemWithOrderFragment)

        if (
          !skipNavigation &&
          !currentlyViewingVersionExists &&
          selectedVersion
        ) {
          history.push(
            versionedRoutes.SHARE_VIEW.VERSION.create({
              shareID: shareIdentifier,
              versionShortId: selectedVersion.shortId,
            })
          )
        }

        apolloClient.writeQuery<
          GetVersionHistoryQuery,
          GetVersionHistoryQueryVariables
        >({
          data: newVersionData,
          query: GetVersionHistoryDocument,
          variables: { shareIdentifier, after: null },
        })
      }

      onCompleted(deletedCount)
    },
    onError: error => {
      onError(error.message)
    },
  })

  return [mutate, ...rest] as const
}
